/**
 * Dashboard Focus Mode Module
 * Handles focus mode initialization and workspace management
 */
export class DashboardFocus {
  constructor(dashboardCore) {
    this.dashboard = dashboardCore;
    this.focusMode = null;
    this.focusWorkspaceState = {
      lastPanelCount: 0,
    };
    this.focusWorkspaceListenerAttached = false;
  }

  async initialize() {
    console.log('[DashboardFocus] Initializing focus mode...');

    try {
      const { initializeFocusMode } = await import('./features/focus-mode-integration.js');

      this.focusMode = await initializeFocusMode({
        data: this.dashboard.core.data,
        taskHub: this.dashboard.taskHub,
        modules: this.dashboard.core.modules
      });

      this.ensureFocusWorkspaceListener();
      this.applyFocusModeInitialState();

      if (this.focusMode?.disable) {
        const originalDisable = this.focusMode.disable;
        this.focusMode.disable = async (...args) => {
          if (this.focusWorkspaceListenerAttached) {
            document.removeEventListener('workspace-changed', this.dashboard.handleFocusWorkspaceChange);
            this.focusWorkspaceListenerAttached = false;
          }
          if (typeof document !== 'undefined' && document.body) {
            delete document.body.dataset.focusPanels;
            delete document.body.dataset.focusLayout;
          }
          return originalDisable.apply(this.focusMode, args);
        };
      }

      console.log('[DashboardFocus] Focus mode initialized successfully!');
    } catch (error) {
      console.error('[DashboardFocus] Failed to initialize focus mode:', error);
    }
  }

  ensureFocusWorkspaceListener() {
    if (this.focusWorkspaceListenerAttached) {
      return;
    }

    document.addEventListener('workspace-changed', this.dashboard.handleFocusWorkspaceChange);
    this.focusWorkspaceListenerAttached = true;
  }

  applyFocusModeInitialState() {
    const workspace = this.focusMode?.workspace;
    const activePanels = Array.isArray(workspace?.activePanels) ? workspace.activePanels : [];
    this.focusWorkspaceState.lastPanelCount = activePanels.length;

    let layout = 'layout-empty';
    if (workspace && typeof workspace.getLayoutClass === 'function') {
      layout = workspace.getLayoutClass();
    } else if (activePanels.length === 1) {
      layout = 'layout-single';
    } else if (activePanels.length === 2) {
      layout = 'layout-split-2';
    } else if (activePanels.length >= 3) {
      layout = 'layout-split-3';
    }

    this.updateFocusWorkspaceMetadata(activePanels.length, layout);

    // When authenticated, start with expanded header to show only header
    // When not authenticated, use collapsed header for focus mode
    const shouldCollapse = !document.body.dataset.auth;
    this.dashboard.setHeaderCollapsed(shouldCollapse);
  }

  updateFocusWorkspaceMetadata(panelCount = 0, layout = 'layout-empty') {
    if (typeof document === 'undefined' || !document.body) {
      return;
    }

    const normalizedCount = Number.isFinite(panelCount) ? panelCount : 0;
    document.body.dataset.focusPanels = String(normalizedCount);

    if (layout) {
      document.body.dataset.focusLayout = layout;
    } else {
      delete document.body.dataset.focusLayout;
    }
  }

  handleFocusWorkspaceChange(event) {
    const detail = event?.detail ?? {};
    const activePanels = Array.isArray(detail.activePanels) ? detail.activePanels : [];
    const panelCount = activePanels.length;
    const previousCount = this.focusWorkspaceState?.lastPanelCount ?? 0;

    this.focusWorkspaceState.lastPanelCount = panelCount;

    const layout =
      detail.layout ||
      (panelCount === 0
        ? 'layout-empty'
        : typeof this.focusMode?.workspace?.getLayoutClass === 'function'
          ? this.focusMode.workspace.getLayoutClass()
          : panelCount === 1
            ? 'layout-single'
            : panelCount === 2
              ? 'layout-split-2'
              : 'layout-split-3');

    this.updateFocusWorkspaceMetadata(panelCount, layout);

    if (panelCount > 0 && previousCount === 0) {
      // When authenticated, keep header expanded; otherwise collapse for focus mode
      const shouldCollapse = !document.body.dataset.auth;
      this.dashboard.setHeaderCollapsed(shouldCollapse);
    } else if (panelCount === 0 && previousCount > 0) {
      // When authenticated, keep header expanded; otherwise collapse for focus mode
      const shouldCollapse = !document.body.dataset.auth;
      this.dashboard.setHeaderCollapsed(shouldCollapse);
    }
  }

  get isEnabled() {
    return Boolean(this.focusMode);
  }

  get workspace() {
    return this.focusMode?.workspace || null;
  }

  get activePanels() {
    return this.workspace?.activePanels || [];
  }
}
